/** Default and named accent swatches for Settings. */
export const DEFAULT_ACCENT = '#E8C547'

export const ACCENT_PRESETS = [
  { id: 'gold', hex: '#E8C547' },
  { id: 'orange', hex: '#FFB77C' },
  { id: 'coral', hex: '#FF8A80' },
  { id: 'rose', hex: '#F2B8B5' },
  { id: 'pink', hex: '#FF80AB' },
  { id: 'violet', hex: '#D0BCFF' },
  { id: 'indigo', hex: '#9FA8DA' },
  { id: 'blue', hex: '#8AB4F8' },
  { id: 'cyan', hex: '#4DD0E1' },
  { id: 'teal', hex: '#4DB6AC' },
  { id: 'green', hex: '#86D994' },
  { id: 'silver', hex: '#C5C6D0' },
] as const

export type AccentPresetId = (typeof ACCENT_PRESETS)[number]['id']

const HEX = /^#([0-9a-fA-F]{6})$/

/**
 * Returns a #RRGGBB color or null if the string is not a 6-digit hex.
 */
export function normalizeHex(value: string): string | null {
  const trimmed = value.trim()
  if (!HEX.test(trimmed)) return null
  return trimmed.toUpperCase()
}

function hexToRgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function rgbToHex(r: number, g: number, b: number): string {
  const to = (c: number) => Math.round(c).toString(16).padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase()
}

function channel(c: number): number {
  const s = c / 255
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
}

function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex)
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** Black or white text that stays readable on `hex`. */
export function contrastOn(hex: string): string {
  return luminance(hex) > 0.45 ? '#1C1B1F' : '#F5F5F5'
}

function mixHex(from: string, to: string, amount: number): string {
  const a = hexToRgb(from)
  const b = hexToRgb(to)
  return rgbToHex(
    a[0] + (b[0] - a[0]) * amount,
    a[1] + (b[1] - a[1]) * amount,
    a[2] + (b[2] - a[2]) * amount,
  )
}

/**
 * Material-style primary tokens derived from a user-picked accent.
 * Light scheme darkens a bright seed so titles stay readable on white.
 */
export function accentCssVars(
  hex: string,
  scheme: 'light' | 'dark' = 'dark',
): Record<string, string> {
  const accent = normalizeHex(hex) ?? DEFAULT_ACCENT
  const primary =
    scheme === 'light' && luminance(accent) > 0.45
      ? mixHex(accent, '#1C1B1F', 0.4)
      : accent
  const mixInto = scheme === 'light' ? '#FFFFFF' : '#1F1F23'
  const mixAmount = scheme === 'light' ? 0.78 : 0.58
  const container = mixHex(accent, mixInto, mixAmount)
  return {
    '--md-sys-color-primary': primary,
    '--md-sys-color-on-primary': contrastOn(primary),
    '--md-sys-color-primary-container': container,
    '--md-sys-color-on-primary-container': contrastOn(container),
  }
}
